import { ArrowRight, Bot, BrainCircuit, CheckCircle2, Clock3, FileText, Loader2, Send, SkipForward, Sparkles, UserRound } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  applyGrowthPlanAdjustment,
  createGrowthCompanionSession,
  dismissGrowthPlanAdjustment,
  generateGrowthCompanionProfile,
  generateAiGrowthTasks,
  generateGrowthPlanAdjustment,
  getAiGrowthPlanVersions,
  getAiGrowthTaskEvaluation,
  getAiGrowthTasks,
  getGrowthCompanionDailyAdvice,
  restoreAiGrowthPlanVersion,
  sendGrowthCompanionMessage,
  updateAiGrowthTask,
} from '../api.js'
import { useAuth } from '../auth-context.js'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import { setPageMetadata } from '../page-metadata.js'

function isI18nKey(value) {
  return typeof value === 'string' && value.startsWith('companion.')
}

function localizeCompanionContent(value, t) {
  if (isI18nKey(value)) return t(`growthCompanion.${value}`)
  if (value === 'Hi, I will get to know you gently. Start with your major, year, or what feels most confusing lately.') {
    return t('growthCompanion.companion.greeting')
  }
  if (value === 'I have enough context for a first profile. You can generate it now, then keep chatting later.') {
    return t('growthCompanion.companion.ready_report')
  }
  return value
}

function MemoryColumn({ emptyText, title, items }) {
  return (
    <article>
      <strong>{title}</strong>
      {items?.length ? items.slice(0, 5).map((item) => <p key={item.id || item.content}>{item.content}</p>) : <p>{emptyText}</p>}
    </article>
  )
}

function MessageBubble({ message, t }) {
  const isUser = message.role === 'user'
  const content = localizeCompanionContent(message.content, t)
  return (
    <article className={`companion-message ${isUser ? 'is-user' : 'is-assistant'}`}>
      <span>{isUser ? <UserRound size={16} /> : <Bot size={16} />}</span>
      <p>{content}</p>
    </article>
  )
}

function ProfileReport({ report, t }) {
  if (!report) return null
  return (
    <section className="companion-report">
      <header>
        <span><FileText size={16} /> {t('growthCompanion.profileTitle')}</span>
        <h2>{report.recommendedDirection || t('growthCompanion.exploring')}</h2>
      </header>
      <div className="companion-report-grid">
        <article><strong>{t('growthCompanion.currentStatus')}</strong><p>{report.currentStatus || '-'}</p></article>
        <article><strong>{t('growthCompanion.recommendedDirection')}</strong><p>{report.recommendedDirection || '-'}</p></article>
        <article><strong>{t('growthCompanion.strengths')}</strong>{(report.strengths || []).map((item) => <p key={item}>{item}</p>)}</article>
        <article><strong>{t('growthCompanion.weaknesses')}</strong>{(report.weaknesses || []).map((item) => <p key={item}>{item}</p>)}</article>
        <article className="is-wide"><strong>{t('growthCompanion.futureAdvice')}</strong>{(report.futureAdvice || []).map((item) => <p key={item}>{t(`growthCompanion.adviceItems.${item}`)}</p>)}</article>
      </div>
    </section>
  )
}

export default function GrowthCompanionPage() {
  const auth = useAuth()
  const { language, t } = useLanguage()
  const [searchParams] = useSearchParams()
  const autoStartRef = useRef(false)
  const [session, setSession] = useState(null)
  const [messages, setMessages] = useState([])
  const [memories, setMemories] = useState([])
  const [report, setReport] = useState(null)
  const [dailyAdvice, setDailyAdvice] = useState(null)
  const [adjustmentDraft, setAdjustmentDraft] = useState(null)
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [adjustmentLoading, setAdjustmentLoading] = useState(false)
  const [adjustmentNotice, setAdjustmentNotice] = useState('')
  const [planVersions, setPlanVersions] = useState([])
  const [restoringVersionId, setRestoringVersionId] = useState('')
  const [aiTasks, setAiTasks] = useState([])
  const [taskActionId, setTaskActionId] = useState('')
  const [taskEvaluation, setTaskEvaluation] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    setPageMetadata({
      title: t('growthCompanion.metaTitle'),
      description: t('growthCompanion.metaDescription'),
    })
  }, [t, language])

  useEffect(() => {
    if (auth?.loading || !auth?.user || searchParams.get('intent') !== 'know-me' || autoStartRef.current) return undefined
    autoStartRef.current = true
    const controller = new AbortController()
    setLoading(true)
    setError('')
    createGrowthCompanionSession({}, controller.signal)
      .then((payload) => {
        setSession(payload.session)
        setMessages(payload.messages || [])
        setMemories(payload.memories || [])
        setReport(payload.report || null)
        setAdjustmentDraft(payload.adjustmentDraft || null)
      })
      .catch((err) => {
        if (err?.name !== 'AbortError') setError(err?.message || t('growthCompanion.errorStart'))
      })
      .finally(() => setLoading(false))
    return () => controller.abort()
  }, [auth?.loading, auth?.user, searchParams, t])

  useEffect(() => {
    if (auth?.loading || !auth?.user) return undefined
    const controller = new AbortController()
    getGrowthCompanionDailyAdvice(controller.signal)
      .then(setDailyAdvice)
      .catch(() => setDailyAdvice(null))
    getAiGrowthPlanVersions(controller.signal)
      .then((payload) => setPlanVersions(payload?.versions || []))
      .catch(() => setPlanVersions([]))
    getAiGrowthTasks(controller.signal)
      .then((payload) => setAiTasks(payload?.tasks || []))
      .catch(() => setAiTasks([]))
    getAiGrowthTaskEvaluation(controller.signal)
      .then(setTaskEvaluation)
      .catch(() => setTaskEvaluation(null))
    return () => controller.abort()
  }, [auth?.loading, auth?.user])

  const userMessageCount = useMemo(() => messages.filter((item) => item.role === 'user').length, [messages])
  const memoryGroups = dailyAdvice?.memorySummary || {
    longTerm: memories.filter((item) => item.type === 'long_term'),
    shortTerm: memories.filter((item) => item.type === 'short_term'),
    preferences: memories.filter((item) => item.type === 'preference'),
  }
  const activePlanVersion = planVersions.find((item) => item.status === 'active') || null

  async function refreshPlanVersions() {
    const payload = await getAiGrowthPlanVersions().catch(() => null)
    if (payload) setPlanVersions(payload.versions || [])
  }

  async function refreshAiTasks() {
    const [payload, evaluation] = await Promise.all([
      getAiGrowthTasks().catch(() => null),
      getAiGrowthTaskEvaluation().catch(() => null),
    ])
    if (payload) setAiTasks(payload.tasks || [])
    setTaskEvaluation(evaluation)
  }

  async function createWeeklyTasks() {
    if (taskActionId) return
    setTaskActionId('generate')
    setError('')
    try {
      const payload = await generateAiGrowthTasks()
      setAiTasks(payload.tasks || [])
      const evaluation = await getAiGrowthTaskEvaluation().catch(() => null)
      setTaskEvaluation(evaluation)
    } catch (err) {
      setError(err?.message || t('growthCompanion.errorWeeklyTasks'))
    } finally {
      setTaskActionId('')
    }
  }

  async function createAdjustmentFromExecution() {
    if (!taskEvaluation?.needsAdjustment || adjustmentLoading) return
    setAdjustmentLoading(true)
    setError('')
    try {
      const reasonText = t(`growthCompanion.evaluationReason.${taskEvaluation.reason}`)
      const payload = await generateGrowthPlanAdjustment({ message: reasonText })
      setAdjustmentDraft(payload.draft || null)
    } catch (err) {
      setError(err?.message || t('growthCompanion.errorAdjustment'))
    } finally {
      setAdjustmentLoading(false)
    }
  }

  async function handleTaskAction(taskId, action, difficultyFeedback = '') {
    if (taskActionId) return
    setTaskActionId(String(taskId))
    setError('')
    try {
      await updateAiGrowthTask(taskId, { action, difficultyFeedback })
      await refreshAiTasks()
    } catch (err) {
      setError(err?.message || t('growthCompanion.errorTaskAction'))
    } finally {
      setTaskActionId('')
    }
  }

  async function startSession() {
    if (!auth?.user || loading) return
    setLoading(true)
    setError('')
    try {
      const payload = await createGrowthCompanionSession()
      setSession(payload.session)
      setMessages(payload.messages || [])
      setMemories(payload.memories || [])
      setReport(payload.report || null)
      setAdjustmentDraft(payload.adjustmentDraft || null)
    } catch (err) {
      setError(err?.message || t('growthCompanion.errorStart'))
    } finally {
      setLoading(false)
    }
  }

  async function submitMessage(event) {
    event.preventDefault()
    if (!session?.id || !input.trim() || loading) return
    const content = input.trim()
    setInput('')
    setLoading(true)
    setError('')
    setMessages((current) => [...current, { id: `local-${Date.now()}`, role: 'user', content }])
    try {
      const payload = await sendGrowthCompanionMessage(session.id, { content })
      setSession(payload.session)
      setMessages(payload.messages || [])
      setMemories(payload.memories || [])
      setReport(payload.report || null)
      setAdjustmentDraft(payload.adjustmentDraft || null)
      const advice = await getGrowthCompanionDailyAdvice().catch(() => null)
      if (advice) setDailyAdvice(advice)
    } catch (err) {
      setError(err?.message || t('growthCompanion.errorSend'))
    } finally {
      setLoading(false)
    }
  }

  async function generateProfile() {
    if (!session?.id || loading) return
    setLoading(true)
    setError('')
    try {
      const payload = await generateGrowthCompanionProfile(session.id)
      setSession(payload.session)
      setMessages(payload.messages || [])
      setMemories(payload.memories || [])
      setReport(payload.report || null)
    } catch (err) {
      setError(err?.message || t('growthCompanion.errorProfile'))
    } finally {
      setLoading(false)
    }
  }

  async function applyAdjustment() {
    if (!adjustmentDraft?.id || adjustmentLoading) return
    setAdjustmentLoading(true)
    setAdjustmentNotice('')
    setError('')
    try {
      await applyGrowthPlanAdjustment(adjustmentDraft.id)
      setAdjustmentDraft(null)
      setAdjustmentNotice(t('growthCompanion.adjustmentApplied'))
      await refreshPlanVersions()
      setAiTasks([])
      setTaskEvaluation(null)
    } catch (err) {
      setError(err?.message || t('growthCompanion.errorAdjustment'))
    } finally {
      setAdjustmentLoading(false)
    }
  }

  async function restorePlanVersion(versionId) {
    if (!activePlanVersion?.id || restoringVersionId) return
    setRestoringVersionId(String(versionId))
    setAdjustmentNotice('')
    setError('')
    try {
      await restoreAiGrowthPlanVersion(versionId, activePlanVersion.id)
      setAdjustmentNotice(t('growthCompanion.versionRestored'))
      await refreshPlanVersions()
      setAiTasks([])
      setTaskEvaluation(null)
    } catch (err) {
      setError(err?.message || t('growthCompanion.errorVersionRestore'))
      await refreshPlanVersions()
    } finally {
      setRestoringVersionId('')
    }
  }

  async function dismissAdjustment() {
    if (!adjustmentDraft?.id || adjustmentLoading) return
    setAdjustmentLoading(true)
    setAdjustmentNotice('')
    setError('')
    try {
      await dismissGrowthPlanAdjustment(adjustmentDraft.id)
      setAdjustmentDraft(null)
      setAdjustmentNotice(t('growthCompanion.adjustmentDismissed'))
    } catch (err) {
      setError(err?.message || t('growthCompanion.errorAdjustment'))
    } finally {
      setAdjustmentLoading(false)
    }
  }

  if (auth?.loading) {
    return <main className="page-container growth-companion-page"><section className="companion-hero"><Loader2 size={18} /> {t('common.loading')}</section></main>
  }

  if (!auth?.user) {
    const guestValues = [
      t('growthCompanion.guestValueClarify'),
      t('growthCompanion.guestValueNext'),
      t('growthCompanion.guestValueSync'),
    ]
    const exampleQuestions = [
      t('growthCompanion.exampleDirection'),
      t('growthCompanion.exampleBehind'),
      t('growthCompanion.exampleInternship'),
    ]

    return (
      <main className="page-container growth-companion-page">
        <section className="companion-hero">
          <div>
            <span><Sparkles size={16} /> {t('growthCompanion.eyebrow')}</span>
            <h1>{t('growthCompanion.loginTitle')}</h1>
            <p>{t('growthCompanion.loginDesc')}</p>
            <div className="companion-guest-actions">
              <Link className="primary-button" to="/login">{t('common.login')} <ArrowRight size={16} /></Link>
              <Link className="secondary-button" to="/explore">{t('growthCompanion.tryExplore')}</Link>
            </div>
          </div>
        </section>
        <section className="companion-guest-preview" aria-label={t('growthCompanion.guestPreviewTitle')}>
          <div>
            <span><BrainCircuit size={16} /> {t('growthCompanion.guestPreviewTitle')}</span>
            <h2>{t('growthCompanion.guestPreviewHeading')}</h2>
          </div>
          <div className="companion-guest-value-list">
            {guestValues.map((item) => <article key={item}>{item}</article>)}
          </div>
        </section>
        <section className="companion-guest-examples">
          <span><Bot size={16} /> {t('growthCompanion.exampleTitle')}</span>
          <div>
            {exampleQuestions.map((item) => <p key={item}>{item}</p>)}
          </div>
        </section>
      </main>
    )
  }

  const adviceKey = dailyAdvice?.advice?.key || 'start_small'

  return (
    <main className="page-container growth-companion-page">
      <section className="companion-hero">
        <div>
          <span><Sparkles size={16} /> {t('growthCompanion.eyebrow')}</span>
          <h1>{t('growthCompanion.title')}</h1>
          <p>{t('growthCompanion.subtitle')}</p>
        </div>
        {!session ? (
          <button className="primary-button" type="button" onClick={startSession} disabled={loading}>
            {loading ? <Loader2 size={16} /> : <Bot size={16} />}
            {t('growthCompanion.start')}
          </button>
        ) : null}
      </section>

      {error ? <p className="student-growth-error">{error}</p> : null}
      {adjustmentNotice ? <p className="student-growth-success" role="status">{adjustmentNotice}</p> : null}

      <section className="companion-dashboard">
        <article>
          <span><BrainCircuit size={16} /> {t('growthCompanion.dailyAdvice')}</span>
          <strong>{t(`growthCompanion.daily.${adviceKey}`)}</strong>
          <p>{dailyAdvice?.advice?.reason || t('growthCompanion.noRecentReason')}</p>
        </article>
        <MemoryColumn title={t('growthCompanion.longTerm')} items={memoryGroups.longTerm} emptyText={t('growthCompanion.emptyLongTerm')} />
        <MemoryColumn title={t('growthCompanion.shortTerm')} items={memoryGroups.shortTerm} emptyText={t('growthCompanion.emptyShortTerm')} />
        <MemoryColumn title={t('growthCompanion.preferences')} items={memoryGroups.preferences} emptyText={t('growthCompanion.emptyPreferences')} />
      </section>

      {session ? (
        <section className="companion-chat">
          <div className="companion-chat-list">
            {messages.map((message) => <MessageBubble key={message.id} message={message} t={t} />)}
            {loading ? <MessageBubble message={{ role: 'assistant', content: t('growthCompanion.thinking') }} t={t} /> : null}
          </div>
          <form className="companion-chat-form" onSubmit={submitMessage}>
            <textarea value={input} onChange={(event) => setInput(event.target.value)} placeholder={t('growthCompanion.placeholder')} />
            <div>
              <button className="secondary-button" type="button" onClick={generateProfile} disabled={loading || userMessageCount < 2}>
                <FileText size={16} /> {t('growthCompanion.generateProfile')}
              </button>
              <button className="primary-button" type="submit" disabled={loading || !input.trim()}>
                <Send size={16} /> {t('growthCompanion.send')}
              </button>
            </div>
          </form>
        </section>
      ) : (
        <section className="companion-empty">
          <Bot size={28} />
          <h2>{t('growthCompanion.notQuestionnaire')}</h2>
          <p>{t('growthCompanion.notQuestionnaireDesc')}</p>
        </section>
      )}

      <ProfileReport report={report} t={t} />

      {adjustmentDraft ? (
        <section className="companion-report" aria-labelledby="growth-adjustment-title">
          <header>
            <span><BrainCircuit size={16} /> {t('growthCompanion.adjustmentTitle')}</span>
            <h2 id="growth-adjustment-title">{adjustmentDraft.summary}</h2>
            <p>{t('growthCompanion.adjustmentUnchanged')}</p>
          </header>
          <div className="companion-report-grid">
            <article>
              <strong>{t('growthCompanion.adjustmentDailyTime')}</strong>
              <p>{adjustmentDraft.suggestedDailyMinutes} {t('growthCompanion.minutes')}</p>
            </article>
            <article>
              <strong>{t('growthCompanion.adjustmentReason')}</strong>
              <p>{adjustmentDraft.reason}</p>
            </article>
            <article className="is-wide">
              <strong>{t('growthCompanion.adjustmentChanges')}</strong>
              {(adjustmentDraft.changes || []).map((item) => <p key={item}>· {item}</p>)}
            </article>
          </div>
          <div className="companion-guest-actions">
            <button className="primary-button" type="button" onClick={applyAdjustment} disabled={adjustmentLoading}>
              {adjustmentLoading ? <Loader2 size={16} /> : <ArrowRight size={16} />}
              {t('growthCompanion.applyAdjustment')}
            </button>
            <button className="secondary-button" type="button" onClick={dismissAdjustment} disabled={adjustmentLoading}>
              {t('growthCompanion.keepCurrentPlan')}
            </button>
          </div>
        </section>
      ) : null}

      {activePlanVersion ? (
        <section className="companion-report" aria-labelledby="ai-weekly-tasks-title">
          <header className="ai-weekly-task-header">
            <div>
              <span><Clock3 size={16} /> {t('growthCompanion.weeklyTasks')}</span>
              <h2 id="ai-weekly-tasks-title">{t('growthCompanion.weeklyTasksTitle')}</h2>
              <p>{t('growthCompanion.weeklyTasksDesc')}</p>
            </div>
            {!aiTasks.length ? (
              <button className="primary-button" type="button" onClick={createWeeklyTasks} disabled={Boolean(taskActionId)}>
                {taskActionId === 'generate' ? <Loader2 size={16} /> : <ArrowRight size={16} />}
                {t('growthCompanion.generateWeeklyTasks')}
              </button>
            ) : null}
          </header>
          {aiTasks.length ? (
            <>
            {taskEvaluation?.needsAdjustment && !adjustmentDraft ? (
              <aside className="ai-task-adjustment-alert" role="status">
                <div>
                  <strong>{t('growthCompanion.adjustmentSuggested')}</strong>
                  <p>{t(`growthCompanion.evaluationReason.${taskEvaluation.reason}`)}</p>
                </div>
                <button className="secondary-button" type="button" onClick={createAdjustmentFromExecution} disabled={adjustmentLoading}>
                  {adjustmentLoading ? <Loader2 size={16} /> : <BrainCircuit size={16} />}
                  {t('growthCompanion.createAdjustmentDraft')}
                </button>
              </aside>
            ) : null}
            <div className="ai-weekly-task-list">
              {aiTasks.map((task) => (
                <article key={task.id} className={`is-${task.status}`}>
                  <div>
                    <span>{task.taskDate} · {task.durationMinutes} {t('growthCompanion.minutes')}</span>
                    <strong>{task.title}</strong>
                    <small>{t(`growthCompanion.taskStatus.${task.status}`)}</small>
                  </div>
                  {task.status === 'pending' ? (
                    <div className="ai-weekly-task-actions">
                      <button type="button" onClick={() => handleTaskAction(task.id, 'complete', 'just_right')} disabled={Boolean(taskActionId)}>
                        <CheckCircle2 size={15} /> {t('growthCompanion.completeTask')}
                      </button>
                      <button type="button" onClick={() => handleTaskAction(task.id, 'feedback', 'too_hard')} disabled={Boolean(taskActionId)}>
                        {t('growthCompanion.tooHard')}
                      </button>
                      <button type="button" onClick={() => handleTaskAction(task.id, 'skip', 'no_time')} disabled={Boolean(taskActionId)}>
                        <SkipForward size={15} /> {t('growthCompanion.skipTask')}
                      </button>
                    </div>
                  ) : null}
                </article>
              ))}
            </div>
            </>
          ) : null}
        </section>
      ) : null}

      {planVersions.length > 1 ? (
        <section className="companion-report" aria-labelledby="growth-plan-history-title">
          <header>
            <span><FileText size={16} /> {t('growthCompanion.versionHistory')}</span>
            <h2 id="growth-plan-history-title">{t('growthCompanion.versionHistoryTitle')}</h2>
            <p>{t('growthCompanion.versionHistoryDesc')}</p>
          </header>
          <div className="growth-plan-version-list">
            {planVersions.map((version) => (
              <article key={version.id}>
                <div>
                  <span>{version.status === 'active' ? t('growthCompanion.currentVersion') : t('growthCompanion.historyVersion')}</span>
                  <strong>{version.target || t('growthCompanion.unnamedPlan')}</strong>
                  <p>{version.adjustment?.summary || version.overview || t('growthCompanion.originalGeneratedPlan')}</p>
                  <small>{version.createdAt ? new Intl.DateTimeFormat(language === 'en' ? 'en' : 'zh-CN', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(version.createdAt)) : '-'}</small>
                </div>
                {version.status !== 'active' ? (
                  <button className="secondary-button" type="button" onClick={() => restorePlanVersion(version.id)} disabled={Boolean(restoringVersionId)}>
                    {restoringVersionId === version.id ? <Loader2 size={16} /> : null}
                    {t('growthCompanion.restoreVersion')}
                  </button>
                ) : null}
              </article>
            ))}
          </div>
        </section>
      ) : null}
    </main>
  )
}
